import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Label, Link } from '@smartshell/ui-kit';
import { createPortal } from 'react-dom';

import { ESubscription, FeaturesContext } from 'features';
import { LINKS } from 'links';
import { LocalizationContext } from 'localization';

import s from './LockTooltip.module.scss';

export const LockTooltip: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const { subscription } = useContext(FeaturesContext);
  const openerRef = useRef<HTMLDivElement>(null);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  const [tooltipContainerEl, setTooltipContainerEl] =
    useState<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    if (!isVisible || !openerRef.current) return;

    const { top, right } = openerRef.current.getBoundingClientRect();

    const el = document.createElement('div');
    el.classList.add(s.tooltip);
    el.style.top = `${top - 8}px`;
    el.style.left = `${right + 8}px`;

    document.body.appendChild(el);
    setTooltipContainerEl(el);

    return () => {
      document.body.removeChild(el);
      setTooltipContainerEl(null);
    };
  }, [isVisible, openerRef.current]);

  useEffect(() => {
    if (!isVisible) return;

    const close = (): void => setIsVisible(false);

    document.addEventListener('click', close);
    document.addEventListener('contextmenu', close);

    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('contextmenu', close);
    };
  }, [isVisible]);

  return (
    <div
      className={s.container}
      ref={openerRef}
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
      onMouseEnter={() => {
        if (tooltipTimer.current) {
          clearTimeout(tooltipTimer.current);
          tooltipTimer.current = null;
        }

        setIsVisible(true);
      }}
      onMouseLeave={() => {
        tooltipTimer.current = setTimeout(() => setIsVisible(false), 50);
      }}
    >
      <Label iconName="LockIcon" color="red" />
      {tooltipContainerEl &&
        createPortal(
          <div className={s.tooltipContent}>
            <span className={s.header}>{t('host.locked.title')}</span>
            {t(
              subscription === ESubscription.FREE
                ? 'host.locked.description.free'
                : 'host.locked.description'
            )}
            <Link
              className={s.footer}
              title={t('more')}
              link={LINKS.subscription[lang]}
            />
          </div>,
          tooltipContainerEl
        )}
    </div>
  );
};
